/**
 * favorites-manager.js
 * Web Component <favorites-manager> — Single source of truth for favorites state.
 * Manages localStorage CRUD, pub/sub events, UI sync, and Favorites page rendering.
 *
 * Dependencies: pubsub.js, constants.js (both loaded via theme.liquid before this file)
 * Storage key: tfp_favorites → Array of { id: number, handle: string }
 */

const FAVORITES_STORAGE_KEY = 'tfp_favorites';

class FavoritesManager extends HTMLElement {
  // ── Lifecycle ──────────────────────────────────────────────────────────────

  connectedCallback() {
    this._favorites = this._load();

    // Event delegation — catches all .wishlist-btn clicks on the page
    this._handleClick = this._onClick.bind(this);
    document.addEventListener('click', this._handleClick);

    // Event delegation — catches all .favorites-card__btn-atc clicks
    this._handleAtcClick = this._onAtcClick.bind(this);
    document.addEventListener('click', this._handleAtcClick);

    // Sync all heart buttons on current page to current state
    this._initButtons();

    // Sync header badge on load
    this._updateHeaderBadge();

    // Listen for favorites changes to update header badge
    subscribe(PUB_SUB_EVENTS.favoritesUpdated, () => this._updateHeaderBadge());

    // Auto-activate Favorites page renderer if section is present
    if (document.querySelector('[data-section="main-favorites"]')) {
      this.renderFavoritesPage('[data-section="main-favorites"]');
    }

    // Theme editor: re-render when the favorites section is reloaded
    this._handleSectionLoad = (event) => {
      const reloaded = event.target || document;
      if (reloaded.querySelector && reloaded.querySelector('[data-section="main-favorites"]')) {
        this.renderFavoritesPage('[data-section="main-favorites"]');
      }
    };
    this._handleSectionSelect = () => {
      if (document.querySelector('[data-section="main-favorites"]')) {
        this.renderFavoritesPage('[data-section="main-favorites"]');
      }
    };
    document.addEventListener('shopify:section:load', this._handleSectionLoad);
    document.addEventListener('shopify:section:select', this._handleSectionSelect);
    document.addEventListener('shopify:block:select', this._handleSectionSelect);
  }

  disconnectedCallback() {
    document.removeEventListener('click', this._handleClick);
    document.removeEventListener('click', this._handleAtcClick);
    if (this._handleSectionLoad) {
      document.removeEventListener('shopify:section:load', this._handleSectionLoad);
    }
    if (this._handleSectionSelect) {
      document.removeEventListener('shopify:section:select', this._handleSectionSelect);
      document.removeEventListener('shopify:block:select', this._handleSectionSelect);
    }
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /**
   * Add a product to favorites (dedup enforced).
   * @param {number} id - Shopify numeric product ID
   * @param {string} handle - Shopify URL handle
   */
  add(id, handle) {
    if (this.isFavorited(id)) return;
    this._favorites = [...this._favorites, { id, handle }];
    this._save(this._favorites);
    this._updateButtons(id, true);
    publish(PUB_SUB_EVENTS.favoritesAdd, { id, handle });
    publish(PUB_SUB_EVENTS.favoritesUpdated, {
      count: this._favorites.length,
      favorites: [...this._favorites],
    });
  }

  /**
   * Remove a product from favorites.
   * @param {number} id - Shopify numeric product ID
   * @param {string} handle - Shopify URL handle
   */
  remove(id, handle) {
    if (!this.isFavorited(id)) return;
    this._favorites = this._favorites.filter((entry) => entry.id !== id);
    this._save(this._favorites);
    this._updateButtons(id, false);
    publish(PUB_SUB_EVENTS.favoritesRemove, { id, handle });
    publish(PUB_SUB_EVENTS.favoritesUpdated, {
      count: this._favorites.length,
      favorites: [...this._favorites],
    });
  }

  /**
   * Returns true if the product ID is currently favorited.
   * @param {number} id
   * @returns {boolean}
   */
  isFavorited(id) {
    return this._favorites.some((entry) => entry.id === Number(id));
  }

  /**
   * Returns a shallow copy of the current favorites array.
   * @returns {Array<{id: number, handle: string}>}
   */
  getAll() {
    return [...this._favorites];
  }

  /**
   * Returns the current favorites count.
   * @returns {number}
   */
  getCount() {
    return this._favorites.length;
  }

  // ── Favorites Page ─────────────────────────────────────────────────────────

  /**
   * Renders the Favorites page grid.
   * Fetches product data via Shopify AJAX API, injects cards, handles empty state.
   * @param {string} containerSelector - CSS selector for the section element
   */
  async renderFavoritesPage(containerSelector) {
    const section = document.querySelector(containerSelector);
    if (!section) return;

    const grid = section.querySelector('.favorites-grid');
    const emptyState = section.querySelector('.favorites-empty');
    if (!grid) return;

    // Clear grid (loading indicator + any previously rendered cards)
    grid.innerHTML = '';

    if (this._favorites.length === 0) {
      if (emptyState) {
        emptyState.removeAttribute('hidden');
        emptyState.style.display = '';
      }
      return;
    }

    // Fetch all products in parallel; silently drop 404s (product deleted/unpublished)
    const results = await Promise.allSettled(
      this._favorites.map((entry) => this._fetchProduct(entry.handle))
    );

    const products = results
      .filter((r) => r.status === 'fulfilled' && r.value !== null)
      .map((r) => r.value);

    if (products.length === 0) {
      if (emptyState) {
        emptyState.removeAttribute('hidden');
        emptyState.style.display = '';
      }
      return;
    }

    // Read aspect ratio from data attribute (set by Liquid schema setting)
    const imageAspectRatio = grid.dataset.imageAspectRatio || '1 / 1';
    const viewBtnLabel = grid.dataset.viewBtnLabel || 'Voir le produit';
    const showAtcBtn = grid.dataset.showAtcBtn === 'true';
    const atcBtnLabel = grid.dataset.atcBtnLabel || 'Ajouter au panier';

    // Render cards into grid
    products.forEach((product) => {
      const card = document.createElement('div');
      card.innerHTML = this._renderCard(product, imageAspectRatio, viewBtnLabel, showAtcBtn, atcBtnLabel);
      grid.appendChild(card.firstElementChild);
    });

    // Sync heart buttons on the newly rendered cards
    this._initButtons();
  }

  // ── Private Methods ────────────────────────────────────────────────────────

  /**
   * Reads and parses localStorage. Returns [] on any error.
   * @returns {Array<{id: number, handle: string}>}
   */
  _load() {
    try {
      const raw = localStorage.getItem(FAVORITES_STORAGE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      // Dedup by id on load
      const seen = new Set();
      return parsed.filter((entry) => {
        if (!entry || typeof entry.id === 'undefined') return false;
        const key = Number(entry.id);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
    } catch (_e) {
      return [];
    }
  }

  /**
   * Serializes and writes favorites to localStorage.
   * @param {Array<{id: number, handle: string}>} favorites
   */
  _save(favorites) {
    try {
      localStorage.setItem(FAVORITES_STORAGE_KEY, JSON.stringify(favorites));
    } catch (_e) {
      // localStorage may be unavailable (private mode, storage full)
    }
  }

  /**
   * Queries all .wishlist-btn on page and syncs their active state.
   */
  _initButtons() {
    document.querySelectorAll('.wishlist-btn').forEach((btn) => {
      const id = btn.dataset.productId;
      if (id) this._setButtonState(btn, this.isFavorited(id));
    });
  }

  /**
   * Updates the favorites count badge in the site header.
   */
  _updateHeaderBadge() {
    const count = this._favorites.length;
    const bubble = document.getElementById('favorites-count-bubble');
    const countEl = document.getElementById('favorites-count');
    const srEl = document.getElementById('favorites-count-sr');
    if (!bubble) return;

    if (count > 0) {
      bubble.removeAttribute('hidden');
      if (countEl) countEl.textContent = count < 100 ? count : '99+';
      if (srEl) srEl.textContent = `${count} favori${count > 1 ? 's' : ''}`;
    } else {
      bubble.setAttribute('hidden', '');
    }
  }

  /**
   * Updates all .wishlist-btn buttons for a given product ID.
   * @param {number} id
   * @param {boolean} active
   */
  _updateButtons(id, active) {
    document
      .querySelectorAll(`.wishlist-btn[data-product-id="${id}"]`)
      .forEach((btn) => this._setButtonState(btn, active));
  }

  /**
   * Sets the active/inactive visual and aria state on a button.
   * @param {HTMLButtonElement} btn
   * @param {boolean} active
   */
  _setButtonState(btn, active) {
    btn.classList.toggle('wishlist-btn--active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    btn.setAttribute(
      'aria-label',
      active ? 'Retirer des favoris' : 'Ajouter aux favoris'
    );
  }

  /**
   * Event delegation handler for .wishlist-btn clicks.
   * Stops propagation to prevent parent card click handlers from interfering.
   * @param {Event} event
   */
  _onClick(event) {
    const btn = event.target.closest('.wishlist-btn');
    if (!btn) return;

    // Prevent parent handlers (card navigation, gallery init) from firing
    event.stopPropagation();
    event.preventDefault();

    const id = Number(btn.dataset.productId);
    const handle = btn.dataset.productHandle || '';
    if (!id) return;

    if (this.isFavorited(id)) {
      this.remove(id, handle);
    } else {
      this.add(id, handle);
    }
  }

  /**
   * Event delegation handler for .favorites-card__btn-atc clicks.
   * Adds the product to cart and opens the cart drawer.
   * @param {Event} event
   */
  _onAtcClick(event) {
    const btn = event.target.closest('.favorites-card__btn-atc');
    if (!btn) return;

    event.stopPropagation();
    event.preventDefault();

    const variantId = btn.dataset.variantId;
    if (!variantId) return;

    this._addToCart(variantId, btn);
  }

  /**
   * Adds a variant to the cart via AJAX and opens the cart drawer.
   * @param {string} variantId - Shopify variant ID
   * @param {HTMLButtonElement} btn - The clicked button (for loading state)
   */
  async _addToCart(variantId, btn) {
    btn.setAttribute('aria-disabled', 'true');

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) return;

      if (typeof window.refreshCartDrawer === 'function') {
        window.refreshCartDrawer(true);
      } else {
        const cartEl = document.querySelector('cart-drawer');
        if (cartEl) {
          cartEl.classList.remove('active');
          setTimeout(() => cartEl.classList.add('active'), 16);
        }
      }
    } catch (_e) {
      // silencieux
    } finally {
      btn.removeAttribute('aria-disabled');
    }
  }

  /**
   * Fetches product data from the Shopify AJAX API.
   * Returns null on any error (404, network failure).
   * @param {string} handle
   * @returns {Promise<Object|null>}
   */
  async _fetchProduct(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) return null;
      return await response.json();
    } catch (_e) {
      return null;
    }
  }

  /**
   * Renders a product card HTML string for the Favorites page grid.
   * Uses <card-preset-gallery> Web Component for multi-image navigation.
   * Matches the visual design of card-top-presets.liquid.
   * @param {Object} product - Shopify product object from AJAX API
   * @returns {string} HTML string
   */
  _renderCard(product, imageAspectRatio = '1 / 1', viewBtnLabel = 'Voir le produit', showAtcBtn = false, atcBtnLabel = 'Ajouter au panier') {
    const price = product.price
      ? (product.price / 100).toLocaleString('fr-FR', {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'EUR',
          minimumFractionDigits: 2,
        })
      : '';

    const comparePrice =
      product.compare_at_price && product.compare_at_price > product.price
        ? (product.compare_at_price / 100).toLocaleString('fr-FR', {
            style: 'currency',
            currency: window.Shopify?.currency?.active || 'EUR',
            minimumFractionDigits: 2,
          })
        : '';

    // Build gallery from product media (images only, keep media_type check)
    const imageMediaItems = (product.media || []).filter(m => m.media_type === 'image');
    const imageCount = imageMediaItems.length;

    // Build slide images HTML
    const slidesHtml = imageMediaItems.map((m, i) => {
      const src = m.src || (m.preview_image && m.preview_image.src) || '';
      return `<img
        class="card-gallery__slide${i === 0 ? ' active' : ''}"
        src="${src}"
        alt="${product.title.replace(/"/g, '&quot;')}"
        width="600" height="600"
        loading="${i === 0 ? 'eager' : 'lazy'}"
        data-index="${i}"
      >`;
    }).join('');

    // Build arrows + dots HTML (only when 2+ images)
    const arrowsHtml = imageCount > 1
      ? `<button type="button" class="arrow prev" aria-label="Image précédente">
          <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z"/>
          </svg>
        </button>
        <button type="button" class="arrow next" aria-label="Image suivante">
          <svg viewBox="0 0 24 24" aria-hidden="true" width="18" height="18">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z"/>
          </svg>
        </button>
        <div class="card-gallery__dots">
          ${imageMediaItems.map((_, i) => `<button type="button" class="card-gallery__dot${i === 0 ? ' active' : ''}" aria-label="Image ${i + 1}" data-index="${i}"></button>`).join('')}
        </div>`
      : '';

    // Fallback to featured_image if no media array
    const fallbackSrc = product.featured_image
      ? product.featured_image.replace(/(\.\w+)$/, '_600x600$1')
      : '';

    const galleryHtml = slidesHtml
      ? `<card-preset-gallery>
          <div class="card-gallery" style="aspect-ratio: ${imageAspectRatio}">${slidesHtml}${arrowsHtml}</div>
        </card-preset-gallery>`
      : fallbackSrc
        ? `<img src="${fallbackSrc}" alt="${product.title.replace(/"/g, '&quot;')}" class="favorites-card__image" loading="lazy" width="600" height="600">`
        : `<div class="favorites-card__image-placeholder"></div>`;

    const cardHtml = `
      <div class="favorites-card">
        <a href="${product.url}" class="favorites-card__image-link" aria-label="${product.title.replace(/"/g, '&quot;')}" style="aspect-ratio: ${imageAspectRatio}">
          ${galleryHtml}
        </a>
        <div class="favorites-card__content">
          <h3 class="favorites-card__title">
            <a href="${product.url}">${product.title}</a>
          </h3>
          <div class="favorites-card__price">
            ${comparePrice ? `<span class="favorites-card__price--compare">${comparePrice}</span>` : ''}
            <span class="favorites-card__price--current">${price}</span>
          </div>
          <div class="favorites-card__actions">
            ${!showAtcBtn ? `<a href="${product.url}" class="favorites-card__btn-view"><span>${viewBtnLabel}</span></a>` : ''}
            ${showAtcBtn ? `<button
              type="button"
              class="favorites-card__btn-atc"
              data-variant-id="${product.variants && product.variants[0] ? product.variants[0].id : ''}"
              data-product-title="${product.title.replace(/"/g, '&quot;')}"
              aria-label="${atcBtnLabel}"
            ><span>${atcBtnLabel}</span></button>` : ''}
            <button
              type="button"
              class="wishlist-btn wishlist-btn--active"
              data-product-id="${product.id}"
              data-product-handle="${product.handle}"
              aria-label="Retirer des favoris"
              aria-pressed="true"
            >
              <span class="wishlist-icon" aria-hidden="true">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                     fill="none" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    `;
    return cardHtml.trim();
  }
}

customElements.define('favorites-manager', FavoritesManager);
